'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import type { AdpopcornPlacementKey } from '@/constants/offerwall';
import { ADPOPCORN_PLACEMENT_IDS } from '@/constants/offerwall';
import { useAdUserId } from '@/hooks/useAdUserId';
import { useVerificationCheck } from '@/hooks/useVerificationCheck';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useAdGuards } from './useAdGuards';
export type AdpopcornStatus = 'unsupported' | 'app-install' | 'login' | 'ready';
interface UseAdpopcornOptions {
  placement: AdpopcornPlacementKey;
}
interface UseAdpopcornPlacementReturn {
  status: AdpopcornStatus;
  placementId: string | null;
  isExternalWebview: boolean;
}
interface UseAdpopcornReturn {
  status: AdpopcornStatus;
  placementId: string | null;
  isExternalWebview: boolean;
  onClick: () => void;
  isOpening: boolean;
}
export const useAdpopcornPlacement = t0 => {
  const $ = _c(4);
  const {
    placement
  } = t0;
  const {
    isMounted,
    isWelloApp,
    isExternalWebview,
    osType
  } = useAdGuards();
  const memberOAuth = useAuthStore(_temp);
  const placementConfig = ADPOPCORN_PLACEMENT_IDS[placement];
  const placementId = osType === "ios" || osType === "android" ? placementConfig[osType] : null;
  let t1;
  bb0: {
    if (!isMounted || isExternalWebview) {
      t1 = "unsupported";
      break bb0;
    }
    if (!isWelloApp) {
      t1 = "app-install";
      break bb0;
    }
    if (!memberOAuth) {
      t1 = "login";
      break bb0;
    }
    if (!placementId) {
      t1 = "unsupported";
      break bb0;
    }
    t1 = "ready";
  }
  const status = t1;
  let t2;
  if ($[0] !== isExternalWebview || $[1] !== placementId || $[2] !== status) {
    t2 = {
      status,
      placementId,
      isExternalWebview
    };
    $[0] = isExternalWebview;
    $[1] = placementId;
    $[2] = status;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const useAdpopcorn = t0 => {
  const $ = _c(23);
  const {
    placement
  } = t0;
  let t1;
  if ($[0] !== placement) {
    t1 = {
      placement
    };
    $[0] = placement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    status,
    placementId,
    isExternalWebview
  } = useAdpopcornPlacement(t1);
  const {
    adUserId,
    isPending: isAdUserIdPending
  } = useAdUserId();
  const {
    toast
  } = useToast();
  const t2 = status === "ready";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    checkVerification,
    redirectToVerification,
    isLoading: isVerificationLoading
  } = useVerificationCheck(t3);
  const setIsShowAppInstallConfirmModal = useGlobalStore(_temp2);
  const setIsShowLoginRedirectionConfirmModal = useGlobalStore(_temp3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      type: MESSAGE_KEYWORD.OPEN_ADPOPCORN_BRIDGE
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    request,
    isPending
  } = useHandleApp(t4);
  let t5;
  if ($[5] !== adUserId || $[6] !== checkVerification || $[7] !== isAdUserIdPending || $[8] !== isVerificationLoading || $[9] !== placementId || $[10] !== redirectToVerification || $[11] !== request || $[12] !== setIsShowAppInstallConfirmModal || $[13] !== setIsShowLoginRedirectionConfirmModal || $[14] !== status || $[15] !== toast) {
    t5 = () => {
      if (status === "app-install") {
        setIsShowAppInstallConfirmModal(true);
        return;
      }
      if (status === "login") {
        setIsShowLoginRedirectionConfirmModal(true);
        return;
      }
      if (status === "ready") {
        if (isVerificationLoading || isAdUserIdPending) {
          toast({
            message: "\uD68C\uC6D0 \uC815\uBCF4\uB97C \uD655\uC778\uD558\uACE0 \uC788\uC5B4\uC694. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uB20C\uB7EC \uC8FC\uC138\uC694",
            type: "info"
          });
          return;
        }
        if (checkVerification()) {
          return;
        }
        if (!adUserId) {
          redirectToVerification();
          return;
        }
        request({
          placementId,
          userId: adUserId
        });
      }
    };
    $[5] = adUserId;
    $[6] = checkVerification;
    $[7] = isAdUserIdPending;
    $[8] = isVerificationLoading;
    $[9] = placementId;
    $[10] = redirectToVerification;
    $[11] = request;
    $[12] = setIsShowAppInstallConfirmModal;
    $[13] = setIsShowLoginRedirectionConfirmModal;
    $[14] = status;
    $[15] = toast;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const onClick = t5;
  let t6;
  if ($[17] !== isExternalWebview || $[18] !== isPending || $[19] !== onClick || $[20] !== placementId || $[21] !== status) {
    t6 = {
      status,
      placementId,
      isExternalWebview,
      onClick,
      isOpening: isPending
    };
    $[17] = isExternalWebview;
    $[18] = isPending;
    $[19] = onClick;
    $[20] = placementId;
    $[21] = status;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
function _temp(state) {
  return state.myInfo?.memberOAuth;
}
function _temp2(state) {
  return state.setIsShowAppInstallConfirmModal;
}
function _temp3(state_0) {
  return state_0.setIsShowLoginRedirectionConfirmModal;
}